import type { IAdminPortalUserProfile } from '@/types/auth'
import type { ICustomer, ICustomerSearchResource } from '@/types/core/Customer'
import type { ILiteSiteFilters, ISiteFilters, ITenantFilters } from '@/types/core/filters'
import type {
  ICreateSiteForm,
  IInviteTeamMembersForm,
  ITeamForm,
  IUpdateSiteForm,
  IUpdateUserProfileForm,
} from '@/types/core/forms'
import type { FullSite, LiteSite } from '@/types/core/Site'
import type { ITeam, ITeamInvitation } from '@/types/core/Teams'
import type { ITenant } from '@/types/core/Tenant'
import type { IResourceSearchFilters } from '@/types/eos/filters'
import type { ICustomerFilters } from '@/types/rc/filters'
import type { CustomerForm, ILegalDocument, ILegalDocumentsAcceptanceForm } from '@/types/rc/forms'
import type { IMinerModel, IModel } from '@/types/rc/miners'
import type { IPage } from '@/types/shared/pagination'
import axios from 'axios'

export async function getAllSites(filters?: ILiteSiteFilters): Promise<LiteSite[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_CORE_SITES_ENDPOINT}/lite`, { params: filters })
  return res.data
}

/**
 * Gets all organization customers with optional query parameters.
 *
 * @param params {fragment: search query, page: request page, per_page: page size}
 * @returns {Promise<any>} an array of organization customers.
 * @throws {AxiosError}
 */
export async function getAllCustomers(params: ICustomerFilters): Promise<IPage<ICustomer>> {
  const res = await axios.get(import.meta.env.VITE_APP_CUSTOMERS_ENDPOINT, {
    params,
  })
  return res.data
}

/**
 * Gets a single customer by ID.
 *
 * @param {number} customerId a valid customer ID.
 * @returns {Promise<any>} the customer associated with `customerId`'s details.
 * @throws {AxiosError}
 */
export async function getSingleCustomer(customerId: number): Promise<ICustomer> {
  const res = await axios.get(`${import.meta.env.VITE_APP_CUSTOMERS_ENDPOINT}/${customerId}`)
  return res.data
}

/**
 * Creates a customer.
 *
 * @param form create customer form.
 * @returns {Promise<any>} created customer.
 * @throws {AxiosError}
 */
export async function createCustomer(form: CustomerForm): Promise<any> {
  const res = await axios.post(import.meta.env.VITE_APP_CUSTOMERS_ENDPOINT, form)
  return res.data
}

/**
 * Updates a customer.
 *
 * @param customerId ID of customer to update.
 * @param form customer details form.
 * @returns updated customer.
 */
export async function updateCustomer(customerId: number, form: CustomerForm): Promise<any> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_CUSTOMERS_ENDPOINT}/${customerId}`, form)
  return res.data
}

/**
 * Deletes a customer given a customer ID.
 *
 * @param id ID of customer to delete.
 */
export async function deleteCustomer(id: number): Promise<void> {
  const res = await axios.delete(`${import.meta.env.VITE_APP_CUSTOMERS_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Gets paginated sites with optional query parameters.
 *
 * @param params {fragment: search query, page: request page, per_page: page size}
 * @returns {Promise<IPage<FullSite>>} an array of sites.
 * @throws {AxiosError}
 */
export async function getSites(params: ISiteFilters): Promise<IPage<FullSite>> {
  const res = await axios.get(import.meta.env.VITE_APP_CORE_SITES_ENDPOINT, {
    params,
  })
  return res.data
}

/**
 * Gets a single site by ID.
 */
export async function getSiteByID(siteID: string): Promise<FullSite> {
  const res = await axios.get(`${import.meta.env.VITE_APP_CORE_SITES_ENDPOINT}/${siteID}`)
  return res.data
}

/**
 * Deletes a site given a site ID.
 */
export async function deleteSite(id: number): Promise<void> {
  const res = await axios.delete(`${import.meta.env.VITE_APP_CORE_SITES_ENDPOINT}`, { data: { id: id } })
  return res.data
}

/**
 * Creates a site.
 *
 * @param form create site form.
 * @returns {Promise<any>} created site.
 * @throws {AxiosError}
 */
export async function createSite(form: ICreateSiteForm): Promise<any> {
  const res = await axios.post(import.meta.env.VITE_APP_CORE_SITES_ENDPOINT, form)
  return res.data
}

/**
 * Updates a site.
 *
 * @param form update site form.
 * @returns {Promise<any>} updated site.
 * @throws {AxiosError}
 */
export async function updateSite(form: IUpdateSiteForm): Promise<any> {
  const res = await axios.put(import.meta.env.VITE_APP_CORE_SITES_ENDPOINT, form)
  return res.data
}

/**
 * Check if a site ID is already in use
 *
 * @param params {site_id: id of the site}
 * @returns {Promise<any>} a dict that tells if the site ID is already in use.
 * @throws {AxiosError}
 */
export async function validateSiteIdUsage(site_id: string): Promise<any> {
  const res = await axios.get(`${import.meta.env.VITE_APP_CORE_SITES_ENDPOINT}/validation`, {
    params: { site_id: site_id },
  })
  return res.data.in_use
}

/**
 * Gets all supported miner models
 *
 * @returns {Promise<any>} an array of miner models.
 * @throws {AxiosError}
 */
export async function getMinerModels(): Promise<IMinerModel[]> {
  const res = await axios.get(import.meta.env.VITE_APP_CORE_MINER_MODELS_ENDPOINT)
  return res.data
}

/**
 * Retrieves the list of miner model families
 * @returns
 */
export async function getMinerModelFamilies(): Promise<string[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_CORE_API}/miner-model-families`)
  return res.data
}

/**
 * Gets all power supply models (data from Strapi).
 *
 * @returns array of power supply models.
 * @throws {AxiosError} Axios error with request or response error details.
 */
export async function getPowerSupplyModels(): Promise<IModel[]> {
  const res = await axios.get<IModel[]>(import.meta.env.VITE_APP_CORE_POWER_SUPPLY_MODELS_ENDPOINT)
  return res.data
}

/**
 * Gets all fan models (data from Strapi).
 *
 * @returns array of fan models.
 * @throws {AxiosError} Axios error with request or response error details.
 */
export async function getFanModels(): Promise<IModel[]> {
  const res = await axios.get<IModel[]>(import.meta.env.VITE_APP_CORE_FAN_MODELS_ENDPOINT)
  return res.data
}

/**
 * Gets all control board models (data from Strapi).
 *
 * @returns array of control board models.
 * @throws {AxiosError} Axios error with request or response error details.
 */
export async function getControlBoardModels(): Promise<IModel[]> {
  const res = await axios.get<IModel[]>(import.meta.env.VITE_APP_CORE_CONTROL_BOARD_MODELS_ENDPOINT)
  return res.data
}

/**
 * Searches resources.
 *
 * @param {IResourceSearchFilters} filters research source filters.
 * @returns {Promise<ICustomerSearchResource[]>}
 */
export async function searchResources(filters?: IResourceSearchFilters): Promise<ICustomerSearchResource[]> {
  const res = await axios.get(import.meta.env.VITE_APP_CORE_SEARCH_RESOURCES_ENDPOINT, { params: filters })
  return res.data
}

/**
 * Gets paginated tenants with optional query parameters.
 *
 * @param params {fragment: search query, page: request page, per_page: page size}
 * @returns {Promise<any>} an array of sites.
 * @throws {AxiosError}
 */
export async function getTenants(params: ITenantFilters): Promise<IPage<ITenant>> {
  const res = await axios.get(import.meta.env.VITE_APP_CORE_TENANTS_ENDPOINT, {
    params,
  })
  return res.data
}

/**
 * Gets all hashboard models (cached from Strapi).
 *
 * @returns array of hashboard models.
 */
export const getHashboardModels = async (): Promise<IModel[]> => {
  const res = await axios.get<IModel[]>(import.meta.env.VITE_APP_CORE_HASHBOARD_MODELS_ENDPOINT)
  return res.data
}

/**
 * Updates a customer's TOS acceptance status.
 */
export const updateLegalDocumentsAcceptance = async (
  tos_accepted: ILegalDocumentsAcceptanceForm
): Promise<ICustomer> => {
  const res = await axios.patch(`${import.meta.env.VITE_APP_CORE_API}/user/legal-documents`, tos_accepted)
  return res.data
}

/**
 * Gets all legal documents.
 */
export const getLegalDocuments = async (): Promise<ILegalDocument[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_CORE_API}/legal-documents`)
  return res.data
}

/**
 * Team endpoints
 */
export const createTeam = async (form: ITeamForm): Promise<any> => {
  const res = await axios.post(`${import.meta.env.VITE_APP_CORE_API}/teams`, form)
  return res.data
}

export const updateTeam = async (id: number, form: ITeamForm): Promise<any> => {
  const res = await axios.put(`${import.meta.env.VITE_APP_CORE_API}/teams/${id}`, form)
  return res.data
}

export const deleteTeam = async (id: number): Promise<void> => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_CORE_API}/teams/${id}`)
  return res.data
}

export const getUserTeam = async (): Promise<ITeam | null> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_CORE_API}/user/team`)
  if (res.status == 204) {
    return null
  }

  return res.data
}

export const getUserTeamInvitations = async (): Promise<ITeamInvitation[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_CORE_API}/user/team-invitations`)
  return res.data
}

export const leaveTeam = async (): Promise<void> => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_CORE_API}/user/team`)
  return res.data
}

export const removeTeamMember = async (teamID: number, memberID: number): Promise<void> => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_CORE_API}/teams/${teamID}/members`, {
    data: { user_id: memberID },
  })
  return res.data
}

export const transferTeamOwnership = async (teamID: number, memberID: number): Promise<void> => {
  const res = await axios.post(`${import.meta.env.VITE_APP_CORE_API}/teams/${teamID}/ownership`, { user_id: memberID })
  return res.data
}

export const inviteTeamMembers = async (teamID: number, form: IInviteTeamMembersForm): Promise<void> => {
  const res = await axios.post(`${import.meta.env.VITE_APP_CORE_API}/teams/${teamID}/invitations`, form)
  return res.data
}

export const manageTeamInvitation = async (invitationID: number, accept: boolean): Promise<void> => {
  const res = await axios.patch(`${import.meta.env.VITE_APP_CORE_API}/user/team-invitations/${invitationID}`, {
    accept,
  })
  return res.data
}

/**
 * User endpoints
 */

/**
 * Logs an Admin Portal user into the BE.
 * @returns {Promise<IAdminPortalUserProfile>} - the user's profile.
 * @throws {AxiosError} - on Axios request error.
 */
export const loginAdminPortalUser = async (): Promise<IAdminPortalUserProfile> => {
  const { data } = await axios.post<IAdminPortalUserProfile>(import.meta.env.VITE_APP_LOGIN_URI)
  return data
}

/**
 * Gets the currently-logged in user's profile (reads token from request).
 *
 * @throws {AxiosError} - on Axios request error.
 */
export const getAdminPortalUserProfile = async () => {
  const { data } = await axios.get(import.meta.env.VITE_APP_PROFILE_ENDPOINT)
  return data
}

/**
 * Updates a user's profile.
 *
 * @param {IUpdateUserProfileForm} form - the update form.
 * @returns {Promise<IAdminPortalUserProfile>} - a Promise containing the updated user profile.
 * @throws {AxiosError} - on Axios request error.
 */
export const updateUserProfile = async (form: IUpdateUserProfileForm): Promise<IAdminPortalUserProfile> => {
  const res = await axios.patch(`${import.meta.env.VITE_APP_CORE_API}/user/profile`, form)
  return res.data
}
